import { clsx } from 'clsx'
import { motion } from 'motion/react'

import { ConversationComposer } from 'modules/coaching/components/ConversationComposer'
import { ProcessingBackgroundVideo } from 'modules/coaching/components/ProcessingBackgroundVideo'
import styles from 'modules/coaching/components/VideoAgentScreen.module.css'

import type { FormEvent, KeyboardEvent } from 'react'

type ConversationProcessingViewProperties = {
  backgroundVideoUrls: string[]
  isBackgroundVideoPaused?: boolean
  isPendingConfirmed: boolean
  pendingPrompt: string
  processingPanelPercentage: number
  processingPanelStatusText: string
  uploadError: string | undefined
  videoCount: number
  videoUrl: string
  onCancelPendingPrompt: () => void
  onPendingPromptChange: (value: string) => void
  onPendingPromptKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void
  onPendingPromptSubmit: (event?: FormEvent) => Promise<void>
  onPendingPromptTextArea: (event: FormEvent<HTMLTextAreaElement>) => void
}

const BACKGROUND_FADE_DURATION_SECONDS = 0.45
const COMPOSER_ENTRY_OFFSET_PIXELS = 12
const COMPOSER_FADE_DURATION_SECONDS = 0.35
const COMPOSER_FADE_DELAY_SECONDS = 0.12

const renderProcessingBackgroundMedia = (
  backgroundVideoUrls: string[],
  videoUrl: string,
  isBackgroundVideoPaused: boolean
) => {
  let sources = backgroundVideoUrls
  if (sources.length === 0 && videoUrl) {
    sources = [videoUrl]
  }
  if (sources.length > 0) {
    return <ProcessingBackgroundVideo isPaused={isBackgroundVideoPaused} sources={sources} />
  }

  return <div className={styles.videoAgentProcessingAmbient} aria-hidden />
}

export const ConversationProcessingView = (properties: ConversationProcessingViewProperties) => {
  /* --- Section 3: Derived Values --- */
  const {
    backgroundVideoUrls,
    isBackgroundVideoPaused = false,
    isPendingConfirmed,
    pendingPrompt,
    processingPanelPercentage,
    processingPanelStatusText,
    uploadError,
    videoCount,
    videoUrl,
    onCancelPendingPrompt,
    onPendingPromptChange,
    onPendingPromptKeyDown,
    onPendingPromptSubmit,
    onPendingPromptTextArea,
  } = properties

  /* --- Section 6: Render --- */
  return (
    <div className={clsx(styles.videoAgentPage, styles.videoAgentProcessingPage)}>
      <motion.div
        className={styles.videoAgentProcessingBackground}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: BACKGROUND_FADE_DURATION_SECONDS, ease: 'easeOut' }}
      >
        {renderProcessingBackgroundMedia(backgroundVideoUrls, videoUrl, isBackgroundVideoPaused)}
        <div className={styles.videoAgentProcessingOverlay} aria-hidden />
      </motion.div>

      <div className={styles.videoAgentLoadingContainer}>
        <motion.div
          className={styles.videoAgentProcessingComposerBadge}
          initial={{ opacity: 0, y: COMPOSER_ENTRY_OFFSET_PIXELS }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: COMPOSER_FADE_DURATION_SECONDS, ease: 'easeOut', delay: COMPOSER_FADE_DELAY_SECONDS }}
        >
          <ConversationComposer
            dataTestId="processing-prompt-input"
            error={uploadError}
            formClassName={styles.videoAgentProcessingComposerForm}
            isCancelVisible={isPendingConfirmed}
            isInputDisabled={isPendingConfirmed}
            isSubmitDisabled={!pendingPrompt.trim()}
            onCancel={onCancelPendingPrompt}
            onInput={onPendingPromptTextArea}
            onInputChange={onPendingPromptChange}
            onInputKeyDown={onPendingPromptKeyDown}
            onSubmit={onPendingPromptSubmit}
            placeholder="Ask about your technique..."
            progressPercentage={processingPanelPercentage}
            statusText={processingPanelStatusText}
            submitTestId="processing-send-button"
            value={pendingPrompt}
            videoCount={videoCount}
          />
          <p data-testid="processing-status-text" className="visually-hidden">
            {processingPanelStatusText}
          </p>
        </motion.div>
      </div>
    </div>
  )
}
